import { customAuth, fetchTokenAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "./logo.svg";

const COMPANY_TOKEN_PLACEHOLDER = "3b-ironscales-company-token";

export const ironscales = defineConnector({
  identifier: "ironscales",
  name: "IRONSCALES",
  tagLine: "Email security and phishing response",
  logo,
  protocol: "http",
  tags: ["security"],
  defaultUrlPattern: () => "https://appapi\\.ironscales\\.com/appapi/.*",
  auth: (app) =>
    fetchTokenAuth({
      request: {
        url: "https://appapi.ironscales.com/appapi/get-token/",
        method: "POST",
        headers: [{ name: "Content-Type", value: "application/json" }],
        rawBody: JSON.stringify({
          key: COMPANY_TOKEN_PLACEHOLDER,
          scopes: (app.configuration.scopes ?? "").split(/\s+/).filter(Boolean),
        }),
        auth: customAuth("loginCredentials", {
          placeholder: COMPANY_TOKEN_PLACEHOLDER,
        }),
      },
      extract: { from: "jsonPath", paths: ["jwt"] },
      apply: { kind: "header", name: "Authorization", scheme: "JWT" },
    }),
  steps: [
    {
      title: "Get your company token",
      items: [
        {
          kind: "markdown",
          content:
            "In IRONSCALES, go to **Settings > General** and copy your **Company Token**. Your company must have API access enabled. The connector exchanges this token for a JWT using the scopes below. See the [IRONSCALES API documentation](https://appapi.ironscales.com/appapi/docs/).",
        },
      ],
    },
    {
      title: "Enter your IRONSCALES details",
      items: [
        {
          kind: "input",
          label: "Company token",
          required: true,
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "input",
          label: "Company ID",
          required: true,
          target: { kind: "configuration", field: "companyId" },
          helperText:
            "The numeric ID of the company to use for the connection test.",
        },
        {
          kind: "input",
          label: "Scopes",
          required: true,
          defaultValue: "company.view",
          target: { kind: "configuration", field: "scopes" },
          helperText:
            "Separate scopes with spaces. Include company.view for the connection test. Add scopes such as company.classify or company.edit only if your workflows need them. Partner keys can use partner.company.view for the test.",
        },
      ],
    },
  ],
  connectionTest: {
    protocol: "http",
    request: ({ companyId }) => ({
      url: `https://appapi.ironscales.com/appapi/incident/${encodeURIComponent(companyId)}/open/`,
      method: "GET",
      expectedStatus: 200,
      expectedBodyContains: '"incident_ids"',
    }),
  },
  usageHint: ({ companyId }) =>
    `Use https://appapi.ironscales.com/appapi/ with company ID ${companyId}. Authentication is added automatically. Available operations depend on the configured scopes; see https://appapi.ironscales.com/appapi/docs/.`,
});
